import { useQuery } from '@tanstack/react-query';
import { CircleDashed, CircleHelp, ExternalLink, Eye, Globe, Search, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Faelligkeit, Meter, Status, StatusPill, cn, formatDatum, formatEuro, statusInfo } from '@elbklar/ui';

import { radarApi } from '@/lib/api';
import { alsLink } from '@/lib/pruefen';
import type { RadarProgram, RadarSourceState } from '@/types';
import { DEADLINE_KIND_TEXT } from './radarText';

/**
 * Kleine Bausteine, die in Liste, Prüfaufträgen, Programmseite und
 * Übersicht gleich aussehen.
 *
 * Seit D2 (Auftrag 010) aus dem Design System: Status und Quellzustand über
 * `STATUS`, Score als `Meter`, Fristen über `Faelligkeit` — ein Wortlaut für
 * Fristen und Aufgaben in allen Apps.
 */

/** Ohne Einstellung warnt das Radar sechs Wochen vor einer Frist (Standard der finance-api). */
const VORWARNUNG_WOCHEN = 6;

/**
 * Tage, ab denen eine Frist in `warning` steht: die Fristwarnung aus den
 * Radar-Einstellungen (`deadline_warning_weeks`, kommt mit `/radar/summary`),
 * nicht fest im Code. Dieselbe Abfrage wie Reiter und Übersichts-Kachel.
 */
export function useVorwarnungTage(): number {
  const zusammenfassung = useQuery({ queryKey: ['radar', 'summary'], queryFn: radarApi.summary });
  return (zusammenfassung.data?.deadline_warning_weeks ?? VORWARNUNG_WOCHEN) * 7;
}

const QUELLE_ICON: Record<RadarSourceState, LucideIcon> = {
  ungeprueft: CircleDashed,
  lesbar: Globe,
  umgezogen: CircleHelp,
  unsicher: CircleHelp,
  js: CircleHelp,
  gesperrt: TriangleAlert,
  quelle_tot: TriangleAlert,
  nicht_lesbar: TriangleAlert,
};

/**
 * Der Quellzustand als Wort mit Icon (bis D2 nur ein Symbol mit Tooltip, am
 * Handy also unlesbar). Alles, was einen Menschen braucht, steht in
 * `warning`; das Icon unterscheidet „Adresse prüfen“ (?) von „händisch
 * prüfen“ (!).
 */
export function Quellzustand({ zustand, className }: { zustand: RadarSourceState; className?: string }) {
  return <Status status={`quelle_${zustand}`} icon={QUELLE_ICON[zustand]} variant="plain" className={className} />;
}

/** Verlangt der Quellzustand, dass jemand nachsieht? Dann zeigt ihn die Liste. */
export function quelleBrauchtJemanden(zustand: RadarSourceState): boolean {
  return statusInfo(`quelle_${zustand}`).tone === 'warning';
}

/** Score als Balken mit Zahl. Die Güte eines Scores ist kein Status: ein Ton für alle Werte. */
export function Score({ score, className }: { score: number | null; className?: string }) {
  if (score === null) return <span className="text-muted-foreground">–</span>;
  return (
    <Meter
      value={score}
      valueLabel={String(score)}
      ariaLabel={`Score ${score} von 100`}
      className={cn('w-28', className)}
    />
  );
}

/**
 * Die Frist eines Programms als Zelle.
 *
 * Eine feste Frist über `Faelligkeit` mit Restlaufzeit; Vergangenes ruhig,
 * denn eine verstrichene Ausschreibung ist kein Versäumnis. „laufend“ und
 * „Aufruf“ sind eigene Aussagen und werden nicht als Lücke dargestellt.
 */
export function FristZelle({
  programm,
  vorwarnungTage,
  relativ = true,
}: {
  programm: RadarProgram;
  vorwarnungTage: number;
  /** Restlaufzeit anhängen; in der Handy-Zeile neben dem Status fehlt der Platz dafür. */
  relativ?: boolean;
}) {
  if (programm.deadline && programm.deadline_kind === 'fest') {
    return <Faelligkeit datum={programm.deadline} relativ={relativ} vorwarnungTage={vorwarnungTage} vergangenRuhig />;
  }
  if (programm.deadline) {
    return (
      <span className="tabular">
        {formatDatum(programm.deadline)}
        <span className="ml-1.5 text-xs text-muted-foreground">{DEADLINE_KIND_TEXT[programm.deadline_kind]}</span>
      </span>
    );
  }
  if (programm.deadline_kind === 'laufend' || programm.deadline_kind === 'aufruf') {
    return <span className="text-muted-foreground">{DEADLINE_KIND_TEXT[programm.deadline_kind]}</span>;
  }
  return <span className="text-muted-foreground">–</span>;
}

/** Ist ein ISO-Datum vorbei (vor heute)? */
function vorbei(wert: string): boolean {
  const heute = new Date();
  const tag = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, '0')}-${String(heute.getDate()).padStart(2, '0')}`;
  return wert.slice(0, 10) < tag;
}

/**
 * Datum einer Veranstaltung, mit Ende, falls es eines gibt. Ein Termin ist
 * keine Frist: ohne Ton, Vergangenes gedämpft.
 */
export function EventDatum({ programm }: { programm: RadarProgram }) {
  if (!programm.event_date) return <span className="text-muted-foreground">–</span>;
  return (
    <span className={cn('tabular', vorbei(programm.event_end_date ?? programm.event_date) && 'text-muted-foreground')}>
      {formatDatum(programm.event_date)}
      {programm.event_end_date && programm.event_end_date !== programm.event_date && (
        <span> – {formatDatum(programm.event_end_date)}</span>
      )}
    </span>
  );
}

/** Betrag bzw. Quote einer Foerderung in einem kurzen Text. */
export function betragText(programm: RadarProgram): string {
  const teile: string[] = [];
  if (programm.amount_min && programm.amount_max) {
    teile.push(`${formatEuro(programm.amount_min)} – ${formatEuro(programm.amount_max)}`);
  } else if (programm.amount_max) {
    teile.push(`bis ${formatEuro(programm.amount_max)}`);
  } else if (programm.amount_min) {
    teile.push(`ab ${formatEuro(programm.amount_min)}`);
  }
  if (programm.funding_rate) teile.push(programm.funding_rate);
  return teile.length ? teile.join(' · ') : '–';
}

/** „Prüfung fällig“ als Wort, wenn ein Programm händisch geprüft werden muss (bis D2 nur ein Auge mit Tooltip). */
export function PruefHinweis({ programm }: { programm: RadarProgram }) {
  if (!programm.manual_check_due) return null;
  return (
    <StatusPill tone="warning" icon={Eye} variant="plain">
      Prüfung fällig
    </StatusPill>
  );
}

/**
 * „Quelle öffnen“ und der fertige Suchbegriff — in den Prüfaufträgen und auf
 * der Programmseite gleich.
 *
 * Nur http(s) wird ein Link; alles andere bleibt Text (kein `javascript:` in
 * href, kein relativer Link aus „www.…“). Beide Links sind am Handy 44 px hoch
 * (README „Mobil“): Anton prüft Prüfaufträge auch unterwegs.
 */
export function QuellLinks({ programm }: { programm: RadarProgram }) {
  const quellAdresse = programm.reference_url || programm.source_url;
  const quellLink = alsLink(programm.reference_url) ?? alsLink(programm.source_url);
  const suchLink = alsLink(programm.search_url);
  return (
    <>
      {quellLink ? (
        <a
          href={quellLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-6 items-center gap-1 font-medium text-link hover:underline touch:min-h-11"
        >
          Quelle öffnen <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
        </a>
      ) : quellAdresse ? (
        <span className="break-all">Quelle: {quellAdresse}</span>
      ) : (
        <span className="text-muted-foreground">kein Link hinterlegt</span>
      )}
      {/* Der Suchbegriff ist oft zwei Zeilen lang: Icon auf Höhe der ersten
          Zeile, Text daneben umbrechend. */}
      {programm.search_url &&
        (suchLink ? (
          <a
            href={suchLink}
            target="_blank"
            rel="noreferrer"
            title="Bei Google suchen"
            className="inline-flex min-h-6 items-start gap-1.5 text-muted-foreground hover:text-foreground hover:underline touch:min-h-11 touch:py-3"
          >
            <Search size={12} strokeWidth={1.75} aria-hidden className="mt-[0.3em] shrink-0" />
            <span className="min-w-0">{programm.search_query}</span>
          </a>
        ) : (
          <span className="inline-flex items-start gap-1.5 text-muted-foreground">
            <Search size={12} strokeWidth={1.75} aria-hidden className="mt-[0.3em] shrink-0" />
            <span className="min-w-0">{programm.search_query}</span>
          </span>
        ))}
    </>
  );
}
